import React, { useMemo, useState } from 'react';

import { useStreamApproval } from '@entities/stream';
import {
  useUserMediafiles,
  useMediaLikeAndUnLike,
  useExcludeMediafile,
  useExcludeMediafileRestore,
} from '@entities/media';
import {
  StreamApprovalInfoTypeFragment,
  PaginationInfoType,
  StreamApprovalQuery,
  Exact,
  MediafileApprovalFragmentFragment,
} from '@shared/api/graphql/generated';
import { QueryResult } from '@apollo/client';

export type ReviewStreamModelContextType = {
  stream?: StreamApprovalInfoTypeFragment;
  mediaFiles: MediafileApprovalFragmentFragment[];
  isMediaFilesLoading: boolean;
  error?: Error;
  loading: boolean;
  paginationInfo?: PaginationInfoType;
  mediaFileActionLoadingMap: Map<string, boolean>;
  requestStream: (id: string) => Promise<QueryResult<StreamApprovalQuery, Exact<{ id: string }>>>;
  requestUserMediaFiles: (id: string, contentblockId: string, page?: number, pageSize?: number) => Promise<void>;
  unLikeMediaFile: (streamId: string, mediafileId: string) => Promise<boolean>;
  likeMediaFile: (streamId: string, mediafileId: string) => Promise<boolean>;
  dislikeMediaFile: (streamId: number, mediafileId: number) => Promise<boolean>;
  dislikeMediaFileRestore: (streamId: number, mediafileId: number) => Promise<boolean>;
  handleMediaFileAction: (key: string, value: boolean) => void;
};

const ReviewStreamModelContext = React.createContext<ReviewStreamModelContextType>({} as ReviewStreamModelContextType);

export const useReviewStreamModelContext = () => React.useContext(ReviewStreamModelContext);

const ReviewStreamModelProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const targetStreamData = useStreamApproval();
  const { request: requestUserMediaFiles, data, loading } = useUserMediafiles();
  const mediaLikeAndUnlike = useMediaLikeAndUnLike();
  const mediaExcludeMediafile = useExcludeMediafile();
  const mediaExcludeMediafileRestore = useExcludeMediafileRestore();

  const likeMediaFile = async (streamId: string, mediafileId: string) => {
    const result = await mediaLikeAndUnlike.likeMediaFile(streamId, mediafileId);
    return Boolean(result.data?.result?.ok);
  };

  const unLikeMediaFile = async (streamId: string, mediafileId: string) => {
    const result = await mediaLikeAndUnlike.unLikeMediaFile(streamId, mediafileId);
    return Boolean(result.data?.result?.ok);
  };

  const dislikeMediaFile = async (streamId: number, mediafileId: number) => {
    const result = await mediaExcludeMediafile.request({ id: String(streamId), mediafileId });
    return Boolean(result);
  };

  const dislikeMediaFileRestore = async (streamId: number, mediafileId: number) => {
    const result = await mediaExcludeMediafileRestore.request({ id: streamId, mediafileId });
    return Boolean(result);
  };

  const [mediaFileActionLoadingMap, setMediaFileActionLoadingMap] = useState<Map<string, boolean>>(new Map());

  const handleMediaFileAction = (key: string, value: boolean) => {
    setMediaFileActionLoadingMap((prevMap) => {
      const updatedMap = new Map(prevMap);

      updatedMap.set(key, value);
      return updatedMap;
    });
  };

  const reviewStreamModel = useMemo(
    (): ReviewStreamModelContextType => ({
      stream: targetStreamData.stream,
      mediaFiles: data.mediafilesList || [],
      isMediaFilesLoading: loading,
      paginationInfo: data.paginationInfo,
      error: targetStreamData.error,
      loading: targetStreamData.loading,
      requestStream: targetStreamData.request,
      requestUserMediaFiles,
      unLikeMediaFile,
      likeMediaFile,
      dislikeMediaFile,
      dislikeMediaFileRestore,
      mediaFileActionLoadingMap,
      handleMediaFileAction,
    }),
    [targetStreamData.stream, data, loading, targetStreamData.error, targetStreamData, requestUserMediaFiles]
  );

  return <ReviewStreamModelContext.Provider value={reviewStreamModel}>{children}</ReviewStreamModelContext.Provider>;
};

export default ReviewStreamModelProvider;
